import { useQuery } from '@tanstack/react-query';

import { repositoryTagsDetails } from '@api/sdk.gen';
import { RegistriesRepositoryTagDetails } from '@api/types.gen';

import { withError } from '@/react-tools/react-query';
import { Environment } from '@/react/portainer/environments/types';

import { Registry } from '../../types/registry';
import { Tag } from '../ItemView/TagsDatatable/types';

import { queryKeys } from './queryKeys';

interface Params {
  registryId: Registry['Id'];
  repository: string;
  environmentId?: Environment['Id'];
  tags: string[];
}

export type TagDetails = Omit<Tag, 'Name'>;

export function useTagsDetails({ tags, ...params }: Params) {
  return useQuery(
    queryKeys.tagsDetails(params.registryId, params.repository, tags),
    () => getTagsDetails({ ...params, tags }),
    {
      enabled: tags.length > 0,
      staleTime: 60 * 1000,
      ...withError('Unable to load tag details'),
    }
  );
}

async function getTagsDetails({
  registryId,
  repository,
  environmentId,
  tags,
}: Params) {
  const { data } = await repositoryTagsDetails({
    path: { id: registryId, repositoryName: repository },
    query: { tag: tags, endpointId: environmentId },
  });

  return Object.fromEntries(
    Object.entries(data).map(([tag, details]) => [tag, toTagDetails(details)])
  );
}

// The generated response type marks every field optional, since swagger
// can't express "present unless the manifest fetch failed". Normalize it to
// TagDetails' stricter Platform shape, matching the omitted-tag fallback
// already used when rendering (see NOT_APPLICABLE in columns/useColumns.ts).
function toTagDetails(details: RegistriesRepositoryTagDetails): TagDetails {
  return {
    Size: details.Size,
    ImageId: details.ImageId,
    Created: details.Created,
    Platforms: details.Platforms?.map((platform) => ({
      Os: platform.Os ?? '',
      Architecture: platform.Architecture ?? '',
    })),
  };
}
